import React, { useState } from 'react';
import clsx from 'clsx';
import { format } from 'date-fns';
import { Waveform } from '@components/Waveform/Release/Waveform';
import { AddToShelfMenuItem } from '@components/basics/Dropdowns/MenuItems/AddToShelfMenuItem';
import { EditFeaturedSoundMenuItem } from '@components/basics/Dropdowns/MenuItems/EditFeaturedSoundMenuItem';
import { ShareMenuItem } from '@components/basics/Dropdowns/MenuItems/ShareMenuItem';
import { ThreeDotsDropdown } from '@components/basics/Dropdowns/ThreeDotsDropdown';
import { PlayIndicatorBars } from '@components/main-components/cards/PlayIndicatorBars';
import { NextFutureImage } from '@components/main-components/image/NextFutureImage';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { PlayButton } from '@components/main-components/play-button/PlayButton';
import { useAuth } from '@context/auth/base';
import { useMediaQuery } from '@context/mediaQuery';
import type { FragmentType } from '@graphql/reactQuery';
import { CollectorFeaturedReleaseFragmentDoc, getFragment, gql } from '@graphql/reactQuery';
import { useGetDominantBackgroundColor } from '@hooks/useGetDominantBackgroundColor';
import { usePlayButton } from '@hooks/usePlayButton';
import { DEFAULT_DOMINANT_BACKGROUND_COLOR } from '@utils/constants';
import { getStaticCoverImage } from '@utils/helpers';
import { webappUriPath } from '@utils/url';
import { FeaturedSoundOverlay } from './FeaturedSoundOverlay';
import { ProfileTopSectionSkeleton } from './ProfileTopSectionSkeleton';
import { SLogoFeaturedSoundOverlay } from './SLogoFeaturedSoundOverlay';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment CollectorFeaturedRelease on CollectedRelease {
    id
    release {
      id
      title
      artist {
        id
        name
        webappUri
      }
      staticCoverImage {
        id
        url
      }
      coverImage {
        id
        url
        dominantColor
      }
      track {
        id
        normalizedPeaks
        duration
      }
      goldenEggNft {
        id
        owner {
          id
        }
      }
      webappUri
      ...WaveformCommentsReleaseInfo
    }
    firstNftOwned {
      id
      updatedAt
    }
  }
`);

export const FeaturedSound = ({
  featuredReleaseData,
  userId,
  isLoading,
}: {
  featuredReleaseData: FragmentType<CollectorFeaturedReleaseFragmentDoc>;
  userId: string;
  isLoading: boolean;
}) => {
  const { authUser } = useAuth();
  const { tablet } = useMediaQuery();
  const featuredReleaseFrag = getFragment(CollectorFeaturedReleaseFragmentDoc, featuredReleaseData);
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);

  const featuredRelease = featuredReleaseFrag.release;

  const {
    isPlaying,
    isLoading: isPlayButtonLoading,
    onPlayClick,
  } = usePlayButton({
    isAlbum: false,
    trackId: featuredRelease.track.id,
  });

  const dominantColor =
    featuredRelease.coverImage.dominantColor === '#FFFFFF' ||
    !featuredRelease.coverImage.dominantColor
      ? DEFAULT_DOMINANT_BACKGROUND_COLOR
      : featuredRelease.coverImage.dominantColor;

  const dominantBackgroundColor = useGetDominantBackgroundColor({ dominantColor });

  const userOwnsGoldenEgg =
    !!featuredRelease.goldenEggNft &&
    !!authUser &&
    featuredRelease.goldenEggNft.owner.id === authUser.id;

  return (
    <div className="mt-6 flex flex-col gap-2 bg-white px-5 md:mt-12 lt:px-0">
      <div className="font-title text-title-m font-medium md:text-title-l">Featured Sound</div>
      {isLoading ? (
        <ProfileTopSectionSkeleton />
      ) : (
        featuredRelease && (
          <div
            className="align-center relative flex h-[100px] w-full items-center overflow-hidden rounded-lg px-4 md:h-[160px]"
            style={{ backgroundColor: dominantBackgroundColor }}
          >
            <div className="absolute left-0 top-0 w-full overflow-hidden">
              <FeaturedSoundOverlay className="relative h-full w-full overflow-hidden" />
            </div>
            <div className="absolute left-[300px] top-0 overflow-hidden">
              <SLogoFeaturedSoundOverlay className="relative overflow-hidden" />
            </div>
            <div className="color-white absolute right-2 top-2 z-above1 md:top-2">
              <ThreeDotsDropdown
                isDropdownOpen={isDropdownOpen}
                setIsDropdownOpen={setIsDropdownOpen}
                variant="inverted"
              >
                {authUser && authUser.id === userId && (
                  <EditFeaturedSoundMenuItem
                    label="Edit Featured Sound"
                    webappUri={authUser.webappUri}
                  />
                )}
                <AddToShelfMenuItem releaseId={featuredRelease.id} source="Collector Profile" />
                <ShareMenuItem releaseId={featuredRelease.id} source="Collector Profile" />
              </ThreeDotsDropdown>
            </div>

            <div className="relative flex w-full gap-2">
              <div className="group relative h-[68px] w-[68px] transition-opacity duration-300 ease-in-out md:h-[127px] md:w-[127px]">
                <div className="h-[68px] w-[68px] overflow-hidden rounded-md md:h-[127px] md:w-[127px]">
                  <NextFutureImage
                    alt="featured release image"
                    src={getStaticCoverImage({
                      coverImage: featuredRelease.coverImage.url,
                      staticCoverImage: featuredRelease.staticCoverImage?.url,
                    })}
                    width={tablet ? 127 : 68}
                    height={tablet ? 127 : 68}
                  />
                </div>
                <div
                  className={clsx(
                    'absolute left-3.5 top-3.5 md:left-11 md:top-11',
                    tablet &&
                      'opacity-0 transition-opacity duration-300 ease-in-out group-hover:opacity-100',
                    !tablet && 'hidden',
                  )}
                >
                  <PlayButton
                    color="white"
                    playing={isPlaying}
                    onClick={onPlayClick}
                    size="s"
                    loading={isPlayButtonLoading}
                  />
                </div>
              </div>

              <div className="flex w-full flex-col gap-4">
                <div className="flex w-[194px] flex-col gap-1 md:w-auto md:max-w-[60%]">
                  <div className="overflow-hidden text-ellipsis whitespace-nowrap font-base text-base-s text-white">
                    <AccessibleLink pathnameUri={webappUriPath(featuredRelease.artist.webappUri)}>
                      {featuredRelease.artist.name}
                    </AccessibleLink>
                  </div>
                  <div className="flex gap-2 whitespace-nowrap font-title text-title-s font-medium text-white">
                    {isPlaying && <PlayIndicatorBars smallBars={!tablet} />}
                    <AccessibleLink
                      className="overflow-hidden text-ellipsis"
                      pathnameUri={webappUriPath(featuredRelease.webappUri)}
                    >
                      {featuredRelease.title}
                    </AccessibleLink>
                  </div>
                  <div className="mt-2 flex items-center gap-1 md:mt-2 md:h-[18px]">
                    {userOwnsGoldenEgg && (
                      <NextFutureImage
                        alt="goldenEggBadge"
                        src="/images/golden-egg-badge.png"
                        className="h-4 w-4"
                        width={16}
                        height={16}
                      />
                    )}
                    {featuredReleaseFrag?.firstNftOwned?.updatedAt && (
                      <p className="font-base text-base-s text-white opacity-60">
                        Collected{' '}
                        {format(
                          new Date(featuredReleaseFrag.firstNftOwned.updatedAt),
                          'MMM dd, yyyy',
                        )}
                      </p>
                    )}
                  </div>
                </div>

                {tablet && (
                  <Waveform
                    track={featuredRelease.track}
                    releaseFrag={featuredRelease}
                    isTrackDisabled={false}
                    showComments={false}
                    showBottomHalf={false}
                    enableTouchHandler
                  />
                )}
              </div>
            </div>
          </div>
        )
      )}
    </div>
  );
};
